import { Spinner } from "@/components/ui/spinner";
import { RoutesConfig } from "@/shared/lib/model/routes";
import { Button } from "@components/ui/button";
import ErrorMessage from "@components/ui/error-message";
import { Input } from "@components/ui/input";
import { Label } from "@components/ui/label";
import { MathHelpers } from "@l_learn/shared";
import { useContextWithId } from "@shared/lib/context";
import { cn } from "@shared/lib/utils";
import Link from "next/link";
import { type UseControllerProps, type UseFormReturn, useController } from "react-hook-form";
import { twJoin } from "tailwind-merge";
import { AuthFormFeaturesLib } from "../lib";
import { AuthFormFeaturesModel } from "../model";

interface AuthFormLayoutProps {
	children: React.ReactNode;
	imageAltText: string;
}

const getImageUrl = () => {
	return `/signInImages/signInImage${MathHelpers.randomInRange(1, 5)}.jpg`;
};

export const AuthFormLayout: React.FC<AuthFormLayoutProps> = ({ children, imageAltText }) => {
	return (
		<div className="grid min-h-full rounded-xl overflow-hidden lg:grid-cols-2">
			<div className="flex flex-col bg-background gap-4 p-6 md:p-10">
				<div className="flex justify-center gap-2 md:justify-start">
					<a href="/" className="flex items-center gap-2 font-medium">
						<div className="size-4 rounded-full bg-accent-foreground" />L learn.
					</a>
				</div>
				<div className="flex flex-1 items-center justify-center">
					<div className="w-full max-w-xs">{children}</div>
				</div>
			</div>
			<div className="relative hidden bg-muted lg:block">
				<img
					src={getImageUrl()}
					alt={imageAltText}
					className="absolute inset-0 h-full w-full object-cover"
				/>
			</div>
		</div>
	);
};

export const SignInAuthMethods = () => {
	return (
		<div className="flex flex-col gap-2">
			<div className="text-center text-sm">
				Don&apos;t have an account?{" "}
				<Link
					prefetch={false}
					className="underline underline-offset-4"
					href={RoutesConfig.signUp.path}
				>
					Sign Up
				</Link>
			</div>
		</div>
	);
};

export const SignUpAuthMethods = () => {
	return (
		<div className="flex flex-col gap-2">
			<div className="text-center text-sm">
				Have an account?{" "}
				<Link
					prefetch={false}
					className="underline underline-offset-4"
					href={RoutesConfig.signIn.path}
				>
					Sign In
				</Link>
			</div>
		</div>
	);
};

export const SubmitAuthFormFeature = (props: { children?: React.ReactNode }) => {
	const { formApi } = useContextWithId(AuthFormFeaturesModel.AuthFormContext);
	return (
		<Button
			disabled={formApi.formState.isSubmitting || !formApi.formState.isValid}
			type="submit"
			className="w-full"
		>
			{formApi.formState.isSubmitting ? (
				<Spinner variant={"muted"} />
			) : (
				props.children || "Login"
			)}
		</Button>
	);
};

export const AuthFormErrorMessage = () => {
	const formCtx = useContextWithId(AuthFormFeaturesModel.AuthFormContext);

	const msg = AuthFormFeaturesLib.extractFirstErrorFromForm(formCtx.formApi);
	return msg ? <ErrorMessage>{msg.message}</ErrorMessage> : null;
};

export const SignUpFormErrorMessage = () => {
	const formCtx = useContextWithId(AuthFormFeaturesModel.SignUpFormContext);

	const msg = AuthFormFeaturesLib.extractFirstErrorFromForm(formCtx.formApi);
	return msg ? <ErrorMessage>{msg.message}</ErrorMessage> : null;
};

export const AuthFormContainer = ({
	className,
	...props
}: React.ComponentPropsWithoutRef<"form">) => {
	return (
		<form noValidate className={cn("flex flex-col gap-6", className)} {...props}>
			{props.children}
		</form>
	);
};

export const AuthFormHeader = (props: { title: string; subTitle: string }) => {
	return (
		<div className="flex flex-col items-center gap-2 text-center">
			<h1 className="text-2xl font-bold">{props.title}</h1>
			<p className="text-balance text-sm text-muted-foreground">{props.subTitle}</p>
		</div>
	);
};

export const ContinueWithBlock = () => {
	return (
		<div className="relative text-center text-sm after:absolute after:inset-0 after:top-1/2 after:z-0 after:flex after:items-center after:border-t after:border-border">
			<span className="relative z-10 bg-background px-2 text-muted-foreground">
				Or continue with
			</span>
		</div>
	);
};

interface AuthFormFieldProps {
	DisplayName: string;
}

export const AuthFormField = (
	props: (
		| UseControllerProps<AuthFormFeaturesModel.SignInFields>
		| UseControllerProps<AuthFormFeaturesModel.SignUpFields>
	) &
		AuthFormFieldProps &
		React.ComponentPropsWithoutRef<"input">
) => {
	const fieldApi = useController(props);
	const { DisplayName, ...rest } = props;
	return (
		<div className="grid gap-2">
			<Label htmlFor={props.name}>{props.DisplayName}</Label>
			<Input
				{...rest}
				id={props.name}
				{...fieldApi.field}
				className={twJoin(fieldApi.fieldState.invalid && "border-red-500")}
			/>
		</div>
	);
};

export const SignUpFormField = (
	props: UseControllerProps<AuthFormFeaturesModel.SignUpFields> &
		AuthFormFieldProps &
		React.ComponentPropsWithoutRef<"input">
) => {
	const fieldApi = useController(props);
	return (
		<div className="grid gap-2">
			<Label htmlFor={props.name}>{props.DisplayName}</Label>
			<Input
				{...props}
				id={props.name}
				{...fieldApi.field}
				className={twJoin(fieldApi.fieldState.invalid && "border-red-500")}
			/>
		</div>
	);
};

export const AuthFormRoot: React.FC<{
	children?: React.ReactNode;
	formApi:
		| UseFormReturn<AuthFormFeaturesModel.SignInFields>
		| UseFormReturn<AuthFormFeaturesModel.SignUpFields>;
}> = (props) => {
	return (
		<AuthFormFeaturesModel.AuthFormContext.Context.Provider
			value={{
				formApi: props.formApi
			}}
		>
			{props.children}
		</AuthFormFeaturesModel.AuthFormContext.Context.Provider>
	);
};
